import {
  AccountCircleOutlined,
  Add,
  ArrowBackOutlined,
  ArrowRightAlt,
  Clear,
  Close,
  Delete,
  Description,
  Edit,
  FilterAltOutlined,
  FilterOutlined,
  FolderOutlined,
  FolderSpecial,
  FolderSpecialTwoTone,
  KeyboardOutlined,
  MoreHoriz,
  Search,
  Chip as ChipIcon,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  FormControlLabel,
  CircularProgress,
  Divider,
  Fab,
  FormControl,
  Icon,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  Card,
  Skeleton,
} from "@mui/material";
import { useApplicationContext } from "../../context/ApplicationContext";
import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { grey, purple } from "@mui/material/colors";
import { font } from "../../styles/font";
import ProjectCard from "./ProjectCard";
import ProjectFilterDrawer from "../drawer/ProjectFilterDrawer";

import { useNavigate, useSearchParams } from "react-router-dom";
import { LightTooltip } from "../styled/CustomComponent";
import EditProjectDrawer from "./EditProject";
import {
  addProject_API,
  deleteProject_API,
  getProjectCategoryList_API,
  getProjectList_API,
  getProjectSubcategoryList_API,
  updateProject_API,
} from "../../api/controller/ProjectController";
import {
  ModulePath,
  Severity,
  USER_ROLE,
} from "../../constant/ApplicationVariables";
import { HttpStatusCode } from "axios";
import useErrorHandler from "../../api/errorHandler/useErrorHandler";
import DeleteProjectDialog from "../dialog/DeleteProjectDialog";
import { getFolderStructureList_API } from "../../api/controller/RequirementController";
import { useTranslation } from "react-i18next";
import { useHotkeys } from "react-hotkeys-hook";
import ShortcutText from "../common/ShortcutText";
import { KeyboardKeys, KeyboardShortcuts } from "../../constant/KeyboardKeys";
import { debounce } from "../../utils/Functions";
import { forwardRef } from "react";
import { VirtuosoGrid } from "react-virtuoso";
import CreateProjectDrawer from "./CreateProject";
import { ProjectCustomIcon } from "../styled/CustomIcons";

export default function ProjectsContainer() {
  const {
    userDetail,
    projectList,
    setProjectList,
    activeProject,
    setActiveProject,
    openCreateProjectDrawer,
    setOpenCreateProjectDrawer,
    openEditProjectDrawer,
    setOpenEditProjectDrawer,
    openDeleteProjectDialog,
    setOpenDeleteProjectDialog,
    handleSnackbar,
    activeProjectSubcategory,
    setActiveProjectSubcategory,
    activeProjectCategory,
    setActiveProjectCategory,
    headerSearchText,
    setHeaderSearchText,
    isUpdateFolderStructure,
    setIsUpdateFolderStructure,
    isUpdateProjectList,
    setIsUpdateProjectList,
    handlePageDetails,
    setCurrentFolderData,
    hasPermission,
  } = useApplicationContext();
  const theme = useTheme();
  const navigate = useNavigate();
  const errorHandler = useErrorHandler();
  const { t } = useTranslation("translation", { keyPrefix: "project" });
  useHotkeys(KeyboardShortcuts.project.create, (e) => handleOpenCreateDialog());

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(
    theme.breakpoints.between("sm", "md")
  );
  const isLaptop = useMediaQuery(
    theme.breakpoints.between("md", "lg")
  );

  const [isLoading, setIsLoading] = useState(false);
  const [openProjectFilter, setOpenProjectFilter] = useState(false);
  const containerRef = useRef(null);

  // Memoize filtered projects to prevent unnecessary recalculations
  const filteredProjects = useMemo(() => {
    let filtered = [...projectList];
    
    // Apply category filter
    if (activeProjectCategory) {
      filtered = filtered.filter(
        (item) => item?.categoryName?.toLowerCase() === activeProjectCategory?.name?.toLowerCase()
      );
    }
    
    // Apply subcategory filter
    if (activeProjectSubcategory) {
      filtered = filtered.filter(
        (item) => item?.subCategoryName?.toLowerCase() === activeProjectSubcategory?.name?.toLowerCase()
      );
    }
    
    // Apply search filter
    if (headerSearchText) {
      filtered = filtered.filter(
        (item) => item.name?.toLowerCase().includes(headerSearchText?.toLowerCase())
      );
    }
    
    // Sort by createdAt descending
    filtered.sort((a, b) => new Date(b?.createdAt) - new Date(a?.createdAt));
    
    return filtered;
  }, [projectList, activeProjectCategory, activeProjectSubcategory, headerSearchText]);

  // Get number of columns based on screen size
  const columnsCount = useMemo(() => {
    if (isMobile) return 1;
    if (isTablet) return 2;
    if (isLaptop) return 3;
    return 4;
  }, [isMobile, isTablet, isLaptop]);

  const handleOpenProject = (projectData, path) => {
    setCurrentFolderData(null);
    localStorage.setItem("activeProject", JSON.stringify(projectData));
    setActiveProject(projectData);
    navigate(path ? path : ModulePath.requirements);
  };

  const handleOpenProjectEdit = (projectData) => {
    setActiveProject(projectData);
    setOpenEditProjectDrawer(true);
  };

  const handleCloseProjectEdit = () => {
    setOpenEditProjectDrawer(false);
    setActiveProject(null);
  };

  const handleConfirmProjectEdit = () => {
    handleCloseProjectEdit();
  };

  const handleOpenCreateDialog = () => {
    setOpenCreateProjectDrawer(true);
  };

  const handleCloseCreateDialog = () => {
    setOpenCreateProjectDrawer(false);
  };

  const handleConfirmCreateDialog = () => {
    handleGetProjectList();
  };

  const handleDeleteProject = (projectData) => {
    setActiveProject(projectData);
    setOpenDeleteProjectDialog(true);
  };

  const handleCloseDeleteProject = () => {
    setOpenDeleteProjectDialog(false);
    setActiveProject(null);
  };

  const handleKeyDown = (e, project) => {
    if (e.key === "Enter") {
      handleOpenProject(project);
    }
    if (e.key === "Delete") {
      handleDeleteProject(project);
    }
    if (e.key === "F2") {
      handleOpenProjectEdit(project);
    }
  };

  const handleClearAllFilters = () => {
    setActiveProjectCategory(null);
    setActiveProjectSubcategory(null);
  };

  const handleClearCategoryFilter = () => {
    setActiveProjectCategory(null);
    setActiveProjectSubcategory(null);
  };

  const handleClearSubcategoryFilter = () => {
    setActiveProjectSubcategory(null);
  };

  const handleGetProjectList = async () => {
    try {
      setIsLoading(true);

      const response = await getProjectList_API();
      console.log(response, "response handleGetProjectList");

      if (response) {
        errorHandler(response, setIsLoading);
      }

      if (response.status === HttpStatusCode.Ok) {
        setProjectList(response.data);
        setIsUpdateProjectList(false);
        if (openCreateProjectDrawer) {
          handleCloseCreateDialog();
        }
      }
    } catch (error) {
      console.log(error, error?.message, "projectreponse errorr");
      handleSnackbar(String(error), Severity.error);
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 1000);
    }
  };

  const handleCreateProject = async (projectData) => {
    if (
      !projectData?.name ||
      !projectData?.description ||
      !projectData?.categoryName ||
      !projectData?.subCategoryName
    ) {
      return handleSnackbar("Please fill all the fields", Severity.error);
    }

    try {
      let body = { ...projectData };

      const response = await addProject_API(body);

      if (response.status === HttpStatusCode.Ok) {
        handleGetProjectList();
        handleCloseCreateDialog();
      }

      if (response) {
        errorHandler(response, setIsLoading);
      }

      setTimeout(() => {}, 1000);

      console.log(response, "projectreponse data");
    } catch (error) {
      console.log(error, "projectreponse errorr");
    }
  };

  const handleUpdateProject = async (projectData) => {
    if (
      !projectData?.name ||
      !projectData?.description ||
      !projectData?.categoryName ||
      !projectData?.subCategoryName
    ) {
      return handleSnackbar("Please fill all the fields", Severity.error);
    }

    try {
      setIsLoading(true);

      let body = {
        currentName: activeProject?.name,
        lastModifiedAt: activeProject?.lastModifiedAt
          ? activeProject?.lastModifiedAt
          : null,
      };

      if (projectData?.name !== activeProject?.name) {
        body.newName = projectData?.name;
      }
      if (projectData?.description !== activeProject?.description) {
        body.description = projectData?.description;
      }

      if (projectData?.categoryName !== activeProject?.categoryName) {
        body.categoryName = projectData?.categoryName;
      }
      if (projectData?.subCategoryName !== activeProject?.subCategoryName) {
        body.subCategoryName = projectData?.subCategoryName;
      }

      if (projectData?.status !== activeProject?.status) {
        body.status = projectData?.status;
      }

      if (projectData?.useCasePrefix !== activeProject?.useCasePrefix) {
        body.useCasePrefix = projectData?.useCasePrefix;
      }

      if (projectData?.userStoryPrefix !== activeProject?.userStoryPrefix) {
        body.userStoryPrefix = projectData?.userStoryPrefix;
      }

      if (projectData?.testCasePrefix !== activeProject?.testCasePrefix) {
        body.testCasePrefix = projectData?.testCasePrefix;
      }

      if (projectData?.releasePrefix !== activeProject?.releasePrefix) {
        body.releasePrefix = projectData?.releasePrefix;
      }

      if (projectData?.sprintPrefix !== activeProject?.sprintPrefix) {
        body.sprintPrefix = projectData?.sprintPrefix;
      }

      const response = await updateProject_API(body);

      if (response) {
        errorHandler(response, setIsLoading);
      }

      if (response.status === HttpStatusCode.Ok) {
        let list = [...projectList];
        let foundIndex = list?.findIndex(
          (item) => item?.name === body.currentName
        );
        if (foundIndex !== -1) {
          list[foundIndex] = response.data;
        }
        setProjectList(list);

        handleCloseProjectEdit();
        handleSnackbar("Project updated successfully", Severity.success);
      }

      console.log(response, "projectreponse data");
    } catch (error) {
      console.log(error, "projectreponse errorr");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      let query = `name=${activeProject?.name}`;

      const response = await deleteProject_API(query);
      console.log(response, "response handleDelete");

      if (response) {
        errorHandler(response);
      }

      if (response.status === HttpStatusCode.Ok) {
        let list = [...projectList];
        list = list?.filter((item) => item?.name !== activeProject?.name);
        setProjectList(list);
        handleCloseDeleteProject();
        handleSnackbar(
          response.message || "Project deleted successfully",
          Severity.success
        );
      }
    } catch (error) {
      console.log(error, error?.message, "projectreponse errorr");
      handleSnackbar(String(error), Severity.error);
    } finally {
      setTimeout(() => {}, 1000);
    }
  };

  useEffect(() => {
    if (
      (projectList?.length === 0 || isUpdateProjectList) &&
      userDetail?.role &&
      userDetail?.role?.toLowerCase() !== USER_ROLE.superadmin?.toLowerCase()
    ) {
      handleGetProjectList();
    }
  }, [projectList?.length, isUpdateProjectList, userDetail?.role]);

  return (
    <Box 
      ref={containerRef}
      className="flex flex-col gap-4 px-0 py-0 relative h-full w-full overflow-hidden"
      sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}
    >
      {/* Header */}
      <Box className="w-full h-fit flex justify-between sticky top-0 z-10 backdrop-blur-sm px-4 py-0">
        <Header
          projectsList={projectList}
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          openFilterDrawer={openProjectFilter}
          setOpenFilterDrawer={setOpenProjectFilter}
        />
      </Box>

      {/* Filter Display */}
      {(activeProjectCategory || activeProjectSubcategory) && (
        <Box className="px-4 flex-shrink-0">
          <Box 
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              p: 2,
              borderRadius: 2,
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: theme.shadows[1],
              mb: 2,
            }}
          >
            <Typography 
              variant="body2" 
              sx={{ 
                fontWeight: 600, 
                color: theme.palette.text.secondary,
                whiteSpace: 'nowrap'
              }}
            >
              Active Filters:
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {activeProjectCategory && (
                <Chip
                  label={`Category: ${activeProjectCategory.name}`}
                  onDelete={handleClearCategoryFilter}
                  deleteIcon={<Clear />}
                  color="primary"
                  variant="outlined"
                  sx={{
                    borderColor: theme.palette.primary.main,
                    color: theme.palette.primary.main,
                    '& .MuiChip-deleteIcon': {
                      color: theme.palette.primary.main,
                      '&:hover': {
                        color: theme.palette.primary.dark,
                      }
                    }
                  }}
                />
              )}
              
              {activeProjectSubcategory && (
                <Chip
                  label={`Subcategory: ${activeProjectSubcategory.name}`}
                  onDelete={handleClearSubcategoryFilter}
                  deleteIcon={<Clear />}
                  color="secondary"
                  variant="outlined"
                  sx={{
                    borderColor: theme.palette.primary.main,
                    color: theme.palette.primary.main,
                    '& .MuiChip-deleteIcon': {
                      color: theme.palette.primary.main,
                      '&:hover': {
                        color: theme.palette.primary.dark,
                      }
                    }
                  }}
                />
              )}
            </Box>
            
            <IconButton
              size="small"
              onClick={handleClearAllFilters}
              sx={{
                ml: 'auto',
                color: theme.palette.error.main,
                '&:hover': {
                  backgroundColor: theme.palette.error.light + '20',
                }
              }}
            >
              <Clear />
            </IconButton>
          </Box>
        </Box>
      )}

      {/* Items list - Fixed VirtuosoGrid configuration */}
      <Box className="flex-1 flex flex-col overflow-hidden px-4" sx={{ minHeight: 0 }}>
        {isLoading ? (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: `repeat(${columnsCount}, minmax(0, 1fr))`,
              gap: "1rem",
              width: "100%",
            }}
          >
            {Array.from({ length: columnsCount * 4 }).map((_, index) => (
              <Skeleton
                key={index}
                variant="rounded"
                width="100%"
                height={200}
                animation="wave"
              />
            ))}
          </div>
        ) : projectList?.length === 0 ? (
          <Box className="grow flex flex-col gap-2 justify-center items-center">
            <ProjectCustomIcon sx={{ fontSize: font.size.fs_1 }} />
            <Typography fontSize={font.size.fs_5}>
              No projects found. Create your first project!
            </Typography>
          </Box>
        ) : filteredProjects.length === 0 ? (
          <Box className="grow flex flex-col gap-0 justify-center items-center">
            <ProjectCustomIcon
              color="secondary"
              sx={{ fontSize: font.size.fs_1 }}
            />
            <Typography fontSize={font.size.fs_4}>
              No projects match your filters
            </Typography>
          </Box>
        ) : (
          <VirtuosoGrid
            data={filteredProjects}
            totalCount={filteredProjects.length}
            initialTopMostItemIndex={0}
          //  overscan={500} // Increased overscan to pre-render more items
            useWindowScroll={false}
            style={{ height: '100%', width: '100%' }}
            increaseViewportBy={{ top: 200, bottom: 200 }} // Pre-render items outside viewport
            components={{
              List: forwardRef(({ style, children, ...props }, ref) => (
                <div
                  ref={ref}
                  {...props}
                  style={{
                    ...style,
                    display: "grid",
                    gridTemplateColumns: `repeat(${columnsCount}, minmax(0, 1fr))`,
                    gap: "1rem",
                    width: "100%",
                  }}
                >
                  {children}
                </div>
              )),
              Item: ({ children, ...props }) => (
                <div
                  {...props}
                  style={{
                    ...props.style,
                    minWidth: 0,
                    height: '100%',
                  }}
                >
                  {children}
                </div>
              ),
            }}
            itemContent={(index, itemData) => {
              if (!itemData?.name) return null;
              
              return (
                <Box
                  key={`project-card-${itemData?.name}-${index}`}
                  aria-label={`Project Card ${itemData?.name}`}
                  tabIndex={0}
                  onKeyDown={(e) => handleKeyDown(e, itemData)}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    cursor: "pointer",
                    overflow: "hidden",
                    borderRadius: 1,
                    "&:hover": {
                      boxShadow: 2,
                      transform: 'translateY(-2px)',
                    },
                    transition: "all 0.2s ease",
                    height: "100%",
                    bgcolor: "card.main",
                  }}
                  bgcolor={theme.palette.card.main}
                  borderRadius={theme.shape.borderRadius}
                >
                  <ProjectCard
                    item={itemData}
                    handleClick={(data, path) =>
                      handleOpenProject(data, path)
                    }
                    handleEditClick={() => handleOpenProjectEdit(itemData)}
                    handleClickDelete={() => handleDeleteProject(itemData)}
                  />
                </Box>
              );
            }}
          />
        )}
      </Box>

      {/* Drawers and Dialogs */}
      <ProjectFilterDrawer
        open={openProjectFilter}
        setOpen={setOpenProjectFilter}
      />
      
      {openCreateProjectDrawer && (
        <CreateProjectDrawer
          open={openCreateProjectDrawer}
          handleClose={handleCloseCreateDialog}
          handleContinue={handleConfirmCreateDialog}
        />
      )}
      {openEditProjectDrawer && (
        <EditProjectDrawer
          open={openEditProjectDrawer}
          handleClose={handleCloseProjectEdit}
          handleContinue={handleConfirmProjectEdit}
        />
      )}
      {openDeleteProjectDialog && (
        <DeleteProjectDialog
          open={openDeleteProjectDialog}
          data={activeProject}
          handleConfirm={() => handleDelete()}
          handleClose={handleCloseDeleteProject}
        />
      )}
    </Box>
  );
}

// Header component (simplified - same as before but without the duplicate filter logic)
function Header({ 
  projectsList, 
  isLoading, 
  setIsLoading,
  openFilterDrawer,
  setOpenFilterDrawer 
}) {
  const {
    userDetail,
    handleSnackbar,
    categoryList,
    setCategoryList,
    activeProjectCategory,
    setActiveProjectCategory,
    activeProjectSubcategory,
    setActiveProjectSubcategory,
    headerSearchText,
    setHeaderSearchText,
    setOpenCreateProjectDrawer,
    hasPermission,
  } = useApplicationContext();
  const theme = useTheme();
  const errorHandler = useErrorHandler();
  const { t } = useTranslation("translation", { keyPrefix: "project" });

  const [loading, setLoading] = useState(false);
  
  const hasProjects = projectsList && projectsList.length > 0;

  let timer;

  const handleChangeSearch = debounce((e) => {
    clearTimeout(timer);
    setLoading(true);
    setIsLoading(true);

    timer = setTimeout(() => {
      setHeaderSearchText(e.target.value);
      setLoading(false);
      setIsLoading(false);
    }, 500);
  }, 300);

  const handleOpenCreateDialog = () => {
    setOpenCreateProjectDrawer(true);
  };

  const handleGetCategoryList = async () => {
    try {
      const response = await getProjectCategoryList_API();

      if (response.status === HttpStatusCode.Ok) {
        setCategoryList(response.data);
      }

      if (response) {
        errorHandler(response);
      }
    } catch (error) {
      console.log(error, "categoryreponse errorr");
    }
  };

  useEffect(() => {
    if (
      categoryList?.length === 0 &&
      userDetail?.role &&
      userDetail?.role?.toLowerCase() !== USER_ROLE.superadmin?.toLowerCase()
    ) {
      handleGetCategoryList();
    }
  }, [categoryList?.length, userDetail?.role]);

  const hasActiveFilters = activeProjectCategory || activeProjectSubcategory;

  return (
    <Box
      className="w-full h-full flex flex-col gap-3 px-4 py-3"
      borderRadius={theme.shape.borderRadius}
      sx={{
        background:
          "linear-gradient(180deg, hsl(var(--background)), hsl(var(--muted)))",
        borderBottom: "1px solid hsl(var(--border))",
      }}
    >
      <Box className="w-full h-fit flex items-center justify-between gap-4">
        <Box className="flex-1 flex items-center gap-4">
          <Box className="flex items-center gap-4">
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                pr: 2,
                borderRight: `1px solid ${theme.palette.divider}`,
                height: 36,
              }}
            >
              <Box>
                <Typography
                  variant="body2"
                  sx={{ lineHeight: 1, color: "hsl(var(--foreground))" }}
                >
                  Projects:
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 24,
                  height: 24,
                  borderRadius: "8px",
                  background: "hsl(var(--primary))",
                  color: "hsl(var(--primary-foreground))",
                  fontWeight: 600,
                  fontSize: "0.6rem",
                  boxShadow: "0 2px 6px hsl(var(--primary) / 0.35)",
                }}
              >
                {projectsList?.length || 0}
              </Box>
            </Box>
          </Box>
        </Box>

        <Box className="flex items-center gap-3" sx={{ flexShrink: 0 }}>
          <Box sx={{ width: 220 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by project name..."
              variant="outlined"
              onChange={handleChangeSearch}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  backgroundColor: theme.palette.background.default,
                  "&:hover": {
                    backgroundColor: theme.palette.action.hover,
                  },
                  "&.Mui-focused": {
                    backgroundColor: theme.palette.background.paper,
                    boxShadow: `0 0 0 2px ${theme.palette.primary.main}20`,
                  },
                },
                "& .MuiOutlinedInput-input": {
                  py: 2,
                },
              }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search
                        sx={{
                          color: theme.palette.text.secondary,
                          fontSize: 20,
                        }}
                      />
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Box>

          <Button
            variant="outlined"
            startIcon={
              <FilterAltOutlined
                sx={{ 
                  fontSize: 18, 
                  color: hasActiveFilters 
                    ? theme.palette.primary.main 
                    : theme.palette.text.secondary,
                }}
              />
            }
            onClick={() => setOpenFilterDrawer(true)}
            disabled={!hasProjects}
            sx={{
              height: 40,
              px: 2.5,
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 500,
              borderColor: hasActiveFilters 
                ? theme.palette.primary.main 
                : "hsl(var(--border))",
              color: hasActiveFilters 
                ? theme.palette.primary.main 
                : theme.palette.text.secondary,
              "&:hover": {
                backgroundColor: hasActiveFilters
                  ? theme.palette.primary.light + '10'
                  : "hsl(var(--muted))",
                borderColor: hasActiveFilters
                  ? theme.palette.primary.main
                  : "hsl(var(--border))",
              },
            }}
          >
            <Box className="flex items-center gap-2">
              <Typography fontSize="0.875rem">Filter</Typography>
              {hasActiveFilters && (
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    backgroundColor: theme.palette.primary.main,
                  }}
                />
              )}
            </Box>
          </Button>

          {hasPermission("Project", "CREATE") && (
            <LightTooltip
              title={
                <Box className="flex items-center gap-2">
                  <Typography fontSize={font.size.fs_5} color="textPrimary">
                    {t("createProject")}
                  </Typography>
                  <ShortcutText
                    keyboardShortcutText={KeyboardShortcuts.project.create}
                    textColor={"textSecondary"}
                  />
                </Box>
              }
              placement="bottom"
            >
              <Button
                variant="contained"
                onClick={handleOpenCreateDialog}
                startIcon={<Add />}
                sx={{
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: 600,
                  height: 40,
                  px: 3,
                  background:
                    "linear-gradient(90deg, hsl(var(--cyan)), hsl(var(--emerald)))",
                  color: "hsl(var(--primary-foreground))",
                  boxShadow: "0 4px 14px hsl(var(--cyan) / 0.35)",
                  "&:hover": {
                    background:
                      "linear-gradient(90deg, hsl(var(--cyan) / 0.9), hsl(var(--emerald) / 0.9))",
                    boxShadow: "0 6px 20px hsl(var(--cyan) / 0.45)",
                  },
                }}
              >
                Create Project
              </Button>
            </LightTooltip>
          )}
        </Box>
      </Box>
    </Box>
  );
}